import type { RunDto } from '@aiai/shared';
import { Callout } from '../../components/Callout';
import { Spinner } from '../../components/Spinner';
import { parseSessionLimitError, splitError } from './runFormat';

/**
 * The stack of run-level callouts rendered directly under the page header:
 * failure/session-limit/OOM diagnostics and the auto-merge outcome banners
 * (merged / awaiting review / conflicts), plus their inline actions. The merge
 * and update-branch actions are wired back to the page's mutations via callbacks.
 */
export function RunAlerts({
  run,
  isBasic = false,
  canMergePr,
  onMergeClick,
  mergePending,
  onUpdateBranch,
  updateBranchPending,
  updateBranchError,
  iterateError,
}: {
  run: RunDto;
  /** Basic (shared-rail) workspace: hide GitHub links the customer can't open. */
  isBasic?: boolean;
  canMergePr: boolean;
  onMergeClick: () => void;
  mergePending: boolean;
  onUpdateBranch: () => void;
  updateBranchPending: boolean;
  updateBranchError: string | null;
  iterateError: string | null;
}) {
  return (
    <>
      {run.errorMessage &&
        (() => {
          const sessionLimit = parseSessionLimitError(run.errorMessage);
          if (sessionLimit) {
            return (
              <Callout tone="warning" kicker="Session limit reached">
                Your Claude subscription&apos;s usage limit was reached
                {sessionLimit.resetAt
                  ? ` — the limit resets at ${sessionLimit.resetAt}`
                  : ''}
                . Wait for the limit to reset, then run the issue again.
              </Callout>
            );
          }
          const { headline, detail } = splitError(run.errorMessage);
          return (
            <Callout tone="danger" kicker="Run failed" detail={detail}>
              {headline}
            </Callout>
          );
        })()}

      {run.oomKilled && (
        <Callout tone="warning" kicker="Ran out of memory">
          This run used more memory than its allotted limit and was stopped
          mid-run. Try a smaller task scope; if it keeps happening, ask your
          administrator to raise the memory allotted to agent runs.
        </Callout>
      )}

      {run.autoMergeStatus === 'auto-merged' && (
        <Callout tone="success" kicker="Auto-merged">
          PR squash-merged automatically — risk{' '}
          <span className="font-semibold text-success-300">LOW</span>, all
          checks passed.
          {run.riskJustification && (
            <span className="ml-1 text-success-300/80">
              ({run.riskJustification})
            </span>
          )}
        </Callout>
      )}

      {run.autoMergeStatus === 'merged' && (
        <Callout tone="success" kicker="Merged">
          {run.autoMergeReason ?? 'PR merged from the app.'}
        </Callout>
      )}

      {run.autoMergeStatus === 'awaiting-review' && (
        <Callout
          tone="warning"
          kicker="Awaiting review"
          actions={
            canMergePr &&
            run.pullRequestUrl &&
            run.pullRequestState === 'open' && (
              <button
                type="button"
                className="btn-success"
                disabled={mergePending}
                onClick={onMergeClick}
              >
                {mergePending && <Spinner className="h-3.5 w-3.5" />}
                Merge PR
              </button>
            )
          }
        >
          {run.autoMergeReason ?? 'PR held for human review.'}
          {run.riskJustification && run.riskLevel === 'high' && (
            <span className="ml-1">({run.riskJustification})</span>
          )}
        </Callout>
      )}
      {run.autoMergeStatus === 'conflicting' && run.pullRequestState === 'open' && (
        <Callout
          tone="danger"
          kicker="Merge conflicts"
          actions={
            canMergePr ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={updateBranchPending}
                onClick={onUpdateBranch}
              >
                {updateBranchPending && <Spinner className="h-3.5 w-3.5" />}
                Update branch
              </button>
            ) : undefined
          }
        >
          This PR has merge conflicts with the base branch — another PR was merged
          while this one was awaiting review. Click{' '}
          <strong>Update branch</strong> to let GitHub attempt an automatic merge,
          or{' '}
          {run.pullRequestUrl && !isBasic ? (
            <a href={run.pullRequestUrl} target="_blank" rel="noreferrer" className="underline">
              resolve the conflicts on GitHub
            </a>
          ) : (
            'resolve the conflicts on GitHub'
          )}.
        </Callout>
      )}
      {updateBranchError && (
        <Callout tone="danger">{updateBranchError}</Callout>
      )}
      {iterateError && (
        <Callout tone="danger">{iterateError}</Callout>
      )}
    </>
  );
}
